// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * Modern exception report summary component.
 *
 * Displays analysis results with visual indicators showing the scale of processing
 * and the subset of assets requiring attention. Designed for B2B fintech SaaS UX.
 * Spans full width to maximize visual impact and information density.
 */
import {
  CheckCircleOutline,
  ErrorOutline,
} from '@/constants/hardfin-standard-icons';
import { useAnimatedCount } from '@/report/hooks/use-animated-count';
import { formatNumber } from '@/utilities/number';
import { Box, Paper, Stack, Typography, useTheme } from '@mui/material';
import { useEffect, useRef } from 'react';

interface ExceptionSummaryStats {
  /**
   * Total number of assets analyzed for exceptions
   */
  totalAnalyzed: number;

  /**
   * Number of assets with exceptions that need attention
   */
  withExceptions: number;
}

interface Props {
  /**
   * Statistics for the exception summary
   */
  stats: ExceptionSummaryStats;

  /**
   * Whether the data is currently loading
   */
  isLoading?: boolean;

  /**
   * Whether to skip animation and show final values immediately
   */
  skipAnimation?: boolean;

  /**
   * Callback fired when the animation completes
   */
  onAnimationComplete?: () => void;
}

/**
 * Modern exception report summary card with visual health indicators.
 *
 * Features:
 * - Full-width layout maximizing space utilization
 * - Prominent display of total assets analyzed
 * - Visual progress bar showing validation rate
 * - Side-by-side metrics for passed and exception assets
 * - Dashboard-style design optimized for B2B fintech UX
 */
export function ExceptionSummaryCard(props: Props) {
  const { stats, isLoading, skipAnimation, onAnimationComplete } = props;
  const theme = useTheme();
  const isReady = !isLoading && stats.totalAnalyzed > 0;

  // Always call hooks, but skip animation if skipAnimation is true
  const animatedTotalFromHook = useAnimatedCount(
    stats.totalAnalyzed,
    isReady && !skipAnimation,
    1500
  );
  const animatedExceptionsFromHook = useAnimatedCount(
    stats.withExceptions,
    isReady && !skipAnimation,
    1500
  );
  const animatedPassedFromHook = useAnimatedCount(
    stats.totalAnalyzed - stats.withExceptions,
    isReady && !skipAnimation,
    1500
  );

  // Use final values immediately if skipAnimation is true, otherwise use animated values
  const animatedTotal = skipAnimation
    ? stats.totalAnalyzed
    : animatedTotalFromHook;
  const animatedExceptions = skipAnimation
    ? stats.withExceptions
    : animatedExceptionsFromHook;
  const animatedPassed = skipAnimation
    ? stats.totalAnalyzed - stats.withExceptions
    : animatedPassedFromHook;

  // Use final target values for stable percentage calculations
  const targetTotal = stats.totalAnalyzed;
  const targetExceptions = stats.withExceptions;
  const targetPassed = targetTotal - targetExceptions;

  // Calculate green bar percentage using animated passed count vs final total
  // This ensures stable animation without oscillation
  const passPercentage =
    targetTotal > 0 ? (animatedPassed / targetTotal) * 100 : 0;

  const finalPassPercentage =
    targetTotal > 0 ? (targetPassed / targetTotal) * 100 : 0;
  const exceptionPercentage =
    targetTotal > 0 ? (animatedExceptions / targetTotal) * 100 : 0;

  // Check if green animation is complete (passed count has reached target)
  const isGreenAnimationComplete = animatedPassed >= targetPassed;

  // Check if all animations are complete
  const isAnimationComplete =
    animatedTotal >= targetTotal &&
    animatedExceptions >= targetExceptions &&
    animatedPassed >= targetPassed;

  // Track if we've already notified about completion to prevent duplicate calls
  const hasNotifiedRef = useRef(false);

  // Notify parent when animation completes (only once per animation cycle)
  useEffect(() => {
    if (
      isAnimationComplete &&
      isReady &&
      onAnimationComplete &&
      !hasNotifiedRef.current
    ) {
      hasNotifiedRef.current = true;
      onAnimationComplete();
    } else if (!isAnimationComplete) {
      // Reset notification flag when animation restarts
      hasNotifiedRef.current = false;
    }
  }, [isAnimationComplete, isReady, onAnimationComplete]);

  return (
    <Paper
      variant="outlined"
      sx={{
        width: '100%',
        borderColor: theme.palette.divider,
      }}
    >
      <Stack
        direction="row"
        padding={3}
        spacing={5}
        alignItems="center"
        flexWrap="wrap"
      >
        {/* Total analyzed - prominent display */}
        <Box sx={{ minWidth: '10rem' }}>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}
          >
            Analysis results
          </Typography>
          <Typography
            variant="h3"
            sx={{
              fontWeight: 600,
              mt: 0.5,
              color: 'text.primary',
              lineHeight: 1.2,
            }}
          >
            {formatNumber(animatedTotal)}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            assets analyzed
          </Typography>
        </Box>

        {/* Passed validation metric */}
        <Box
          sx={{
            minWidth: 180,
            p: 2.5,
            borderRadius: 2,
            backgroundColor: 'success.main',
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
            <CheckCircleOutline
              sx={{ fontSize: 24, color: 'success.contrastText' }}
            />
            <Typography
              variant="subtitle2"
              color="success.contrastText"
              fontWeight={500}
            >
              Passed validation
            </Typography>
          </Stack>
          <Typography
            variant="h4"
            textAlign="right"
            color="success.contrastText"
            sx={{
              fontWeight: 600,
              lineHeight: 1.2,
            }}
          >
            {formatNumber(animatedPassed)}
          </Typography>
          <Typography
            component="div"
            variant="caption"
            textAlign="right"
            color="success.contrastText"
            sx={{ mt: 0.5 }}
          >
            {passPercentage.toFixed(1)}% of total
          </Typography>
        </Box>

        {/* Visual progress bar */}
        <Box sx={{ flex: 1, minWidth: 200 }}>
          <Box
            sx={{
              mb: 1.5,
              height: 12,
              borderRadius: 1.5,
              overflow: 'hidden',
              backgroundColor: theme.palette.grey[200],
              position: 'relative',
              width: '100%',
            }}
          >
            {/* Green bar (passed) - animates from left to right */}
            <Box
              sx={{
                position: 'absolute',
                left: 0,
                top: 0,
                height: '100%',
                width: `${passPercentage}%`,
                backgroundColor: 'success.main',
              }}
            />
            {/* Red bar (exceptions) - appears after green animation completes */}
            {isGreenAnimationComplete && exceptionPercentage > 0 && (
              <Box
                sx={{
                  position: 'absolute',
                  left: `${finalPassPercentage}%`,
                  top: 0,
                  height: '100%',
                  width: `${exceptionPercentage}%`,
                  backgroundColor: 'error.main',
                  transition: 'opacity 0.3s ease',
                }}
              />
            )}
          </Box>
          <Stack direction="row" justifyContent="space-between" spacing={2}>
            <Typography variant="caption" color="text.secondary">
              {passPercentage.toFixed(0)}% passed validation
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {exceptionPercentage.toFixed(0)}% require attention
            </Typography>
          </Stack>
        </Box>

        {/* Exceptions metric */}
        <Box
          sx={{
            minWidth: 180,
            p: 2.5,
            borderRadius: 2,
            backgroundColor:
              isGreenAnimationComplete && exceptionPercentage === 0
                ? 'error.light'
                : 'error.main',
            opacity:
              isGreenAnimationComplete && exceptionPercentage === 0 ? 0.5 : 1,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
            <ErrorOutline sx={{ fontSize: 24, color: 'error.contrastText' }} />
            <Typography
              variant="subtitle2"
              color="error.contrastText"
              fontWeight={500}
            >
              Require attention
            </Typography>
          </Stack>
          <Typography
            variant="h4"
            textAlign="right"
            color="error.contrastText"
            sx={{ fontWeight: 600, lineHeight: 1.2 }}
          >
            {formatNumber(animatedExceptions)}
          </Typography>
          <Typography
            variant="caption"
            component="div"
            textAlign="right"
            color="error.contrastText"
            sx={{ mt: 0.5 }}
          >
            {exceptionPercentage.toFixed(1)}% of total
          </Typography>
        </Box>
      </Stack>
    </Paper>
  );
}
